import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { AxiosError } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { resetPassword } from "../../auth0/resetPassword";
import auth0Config from "../../utils/auth0ConfigHelper";
import { pushCustomEvent } from "../../utils/customEventHelper";
import { getLocaleFromCookie } from "../../utils/getLocaleFromCookie";
import {
  hasLowerCase,
  hasNumbers,
  hasSpecialCharacters,
  hasUpperCase,
  maxLength,
  minLength,
} from "../../utils/passwordRules";
import FormButton from "../common/FormButton";
import FormInputNewPassword from "../common/FormInputNewPassword";
import FormInputPassword from "../common/FormInputPassword";
import PasswordStrengthIndicator from "../common/PasswordStrengthIndicator";
import LoginFormLogo from "../login/components/LoginFormLogo";

type ValidationSchema = (t: Function) => Yup.ObjectSchema<
  object & {
    password: string;
    confirmPassword: string;
  }
>;
const validationSchema: ValidationSchema = (t) =>
  Yup.object().shape({
    password: Yup.string()
      .required(t("error.validation.newPassword.required"))
      .concat(minLength(t))
      .concat(maxLength(t))
      .concat(hasUpperCase(t))
      .concat(hasLowerCase(t))
      .concat(hasNumbers(t))
      .concat(hasSpecialCharacters(t)),
    confirmPassword: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        t("error.validation.newPassword.mustMatch"),
      )
      .required(t("error.validation.newPassword.required"))
      .concat(minLength(t))
      .concat(maxLength(t))
      .concat(hasUpperCase(t))
      .concat(hasLowerCase(t))
      .concat(hasNumbers(t))
      .concat(hasSpecialCharacters(t)),
  });

type ResetPasswordFormProps = {
  password?: string;
};

const ResetPassword: React.FC<ResetPasswordFormProps> = () => {
  const { t } = useTranslation();
  const searchParams = new URLSearchParams(window.location.search);
  const utmCampaign = searchParams.get("utm_campaign");
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    const sendLanguageSelection = (): void => {
      if (utmCampaign) {
        let locale;
        const login_hint = auth0Config?.extraParams?.login_hint;
        if (login_hint) {
          ({ locale } = JSON.parse(login_hint));
        } else {
          locale = getLocaleFromCookie() ?? "en_??";
        }
        pushCustomEvent(`${utmCampaign}|${locale}`);
      }
    };

    sendLanguageSelection();
  }, [utmCampaign]);

  interface InitialValues {
    password: string;
    confirmPassword: string;
    apiResponse: string;
  }
  const initialValues: InitialValues = {
    password: "",
    confirmPassword: "",
    apiResponse: "",
  };

  type HandleSubmit = (
    values: InitialValues,
    actions: FormikHelpers<InitialValues>,
  ) => Promise<void>;
  const handleSubmit: HandleSubmit = async (values, actions) => {
    const { password, confirmPassword } = values;

    try {
      await resetPassword(password, confirmPassword);
      setResetSuccess(true);
    } catch (error) {
      const {
        response: {
          status,
          data: { name },
        },
      } = error as Required<AxiosError>;
      if (status === 400) {
        if (name === "PasswordHistoryError") {
          actions.setErrors({
            apiResponse: t("error.validation.newPassword.mustNotBeSame"),
          });
        } else {
          actions.setErrors({
            apiResponse: t("error.validation.newPassword.required"),
          });
        }
      } else {
        actions.setErrors({
          apiResponse: t("passwordManagement.passwordResetGenericError"),
        });
      }
    }
    pushCustomEvent("resetPassword");
  };

  return (
    <Formik
      validationSchema={validationSchema(t)}
      validateOnMount={false}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, errors }): JSX.Element => (
        <Form data-testid="resetPasswordForm">
          <Card>
            <LoginFormLogo />
            <CardHeader
              data-testid="resetPasswordTitle"
              title={t("passwordManagement.newPassword")}
              titleTypographyProps={{
                align: "center",
                gutterBottom: false,
                paragraph: false,
                variant: "h1",
              }}
            ></CardHeader>
            <CardContent>
              <Grid
                container
                direction="column"
                alignContent="stretch"
                wrap="nowrap"
                spacing={2}
              >
                <Grid item>
                  <FormInputNewPassword
                    data-testid="password"
                    name="password"
                    label={t("label.password")}
                  />
                </Grid>

                <Grid item>
                  <PasswordStrengthIndicator />
                </Grid>
                <Grid item>
                  <FormInputPassword
                    data-testid="confirmPassword"
                    name="confirmPassword"
                    label={t("passwordManagement.confirmNewPassword")}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                padding: 16,
              }}
            >
              <FormButton
                waiting={isSubmitting}
                label={t("button.continue")}
                testId="sendButton"
              />
            </CardActions>

            {errors.apiResponse && (
              <CardActions>
                <Alert
                  severity="error"
                  data-testid="generalError"
                  style={{ margin: 10, width: "100%" }}
                >
                  {errors.apiResponse}
                </Alert>
              </CardActions>
            )}

            {resetSuccess && (
              <CardActions>
                <Alert
                  severity="success"
                  data-testid="resetPasswordSuccess"
                  style={{ margin: 10, width: "100%" }}
                >
                  {t("passwordManagement.passwordResetSuccess")}
                </Alert>
              </CardActions>
            )}
          </Card>
        </Form>
      )}
    </Formik>
  );
};

export default ResetPassword;
